
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Notification, NotificationPreferences } from '@/types/notification';
import { supabase } from '@/lib/supabase/client';
import { toast } from 'sonner';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  preferences: NotificationPreferences | null;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  updatePreferences: (preferences: Partial<NotificationPreferences>) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  // Usa a instância singleton do Supabase

  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    const fetchNotifications = async () => {
      const { data: notificationsData, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar notificações:', error);
        return;
      }

      setNotifications(notificationsData);
    };

    const fetchPreferences = async () => {
      const { data: preferencesData, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao buscar preferências:', error);
        return;
      }

      setPreferences(preferencesData);
    };

    fetchNotifications();
    fetchPreferences();

    // Inscrever para atualizações em tempo real
    const notificationsSubscription = supabase
      .channel('notifications')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
      }, (payload) => {
        const newNotification = payload.new as Notification;
        setNotifications(prev => [newNotification, ...prev]);
        
        // Mostrar toast para novas notificações
        toast(newNotification.title, {
          description: newNotification.message,
          action: {
            label: "Ver",
            onClick: () => window.location.href = newNotification.link || '/notifications',
          },
        });
      })
      .subscribe();

    return () => {
      notificationsSubscription.unsubscribe();
    };
  }, [supabase]);

  const markAsRead = async (notificationId: string) => {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId);

    if (error) {
      console.error('Erro ao marcar notificação como lida:', error);
      return;
    }

    setNotifications(prev =>
      prev.map(n => (n.id === notificationId ? { ...n, read: true } : n))
    );
  };

  const markAllAsRead = async () => {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('read', false);

    if (error) {
      console.error('Erro ao marcar todas notificações como lidas:', error);
      return;
    }

    setNotifications(prev =>
      prev.map(n => ({ ...n, read: true }))
    );
  };

  const updatePreferences = async (newPreferences: Partial<NotificationPreferences>) => {
    const { error } = await supabase
      .from('notification_preferences')
      .upsert({
        ...preferences,
        ...newPreferences,
        updated_at: new Date().toISOString(),
      });

    if (error) {
      console.error('Erro ao atualizar preferências:', error);
      return;
    }

    setPreferences(prev => prev ? { ...prev, ...newPreferences } : null);
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        preferences,
        markAsRead,
        markAllAsRead,
        updatePreferences,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
} 